import {
  V2Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  VisuallyHidden,
  Button,
  ScrollArea,
  DialogTrigger,
  cn,
  buttonVariants,
} from "web-design-system";
import { Route } from "@/routes/(auth)/_headed/clients/$id/planning";
import { useClient, useGoalProposals } from "creso-core/hooks";
import { FileSpreadsheetIcon, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Client, Goal } from "creso-core/types";
import { GoalIcon } from "web-core";
import { ProposalCard } from "../proposals/proposal-card";

export function GoalProposalDialog(props: { goal: Goal }) {
  const { goal } = props;
  const clientId = goal.client;
  const [open, setOpen] = useState(false);

  const { data: client, isLoading: isLoadingClient } = useClient(clientId);

  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data: proposals, isLoading: isLoadingGoalProposals } =
    useGoalProposals(
      { goalId: props.goal.id },
      { enabled: !!props.goal.id && open }
    );

  const isLoading = isLoadingClient || isLoadingGoalProposals;

  useEffect(() => {
    if (search.modal) {
      setOpen(false);
    }
  }, [search.modal]);

  return (
    <V2Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        className={cn(
          "px-2 font-normal cursor-pointer h-auto",
          buttonVariants({
            variant: "link",
          })
        )}
      >
        <FileSpreadsheetIcon />
        {goal.proposals_count} proposals for this goal
      </DialogTrigger>
      <DialogContent className="h-150 gap-0 py-4 px-0 w-128 flex flex-col overflow-hidden">
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>Goal Proposals</DialogTitle>
          </DialogHeader>
        </VisuallyHidden>

        {/* Goal Header */}
        <div className="flex items-center gap-3 px-5">
          {goal?.category && <GoalIcon category={goal?.category} />}
          <span className="font-semibold text-base flex-1 truncate">
            {goal?.name}
          </span>
          <Button
            variant="link"
            className="px-0"
            onClick={() => {
              navigate({
                to: ".",
                search: {
                  client_id: Number(clientId),
                  goal_id: goal.id,
                  modal: "goals",
                },
                params: { id: clientId.toString() },
              });
            }}
          >
            View Goal
          </Button>
        </div>

        {/* Proposals List */}
        <ScrollArea className="flex-1 min-h-100 px-5">
          <div className="flex flex-col gap-4 py-4">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {!isLoading && (!proposals || proposals.length === 0) && (
              <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                No proposals for this goal yet
              </div>
            )}

            {proposals?.map((proposal) => (
              <ProposalCard
                withIcon={false}
                key={proposal.id}
                proposal={proposal}
                client={client as Client}
              />
            ))}
          </div>
        </ScrollArea>

        {/* Create Proposal Button */}
        <div className="pt-5">
          <Button
            variant="outline"
            className="justify-start w-fit gap-2 ml-5"
            onClick={() => {
              navigate({
                to: ".",
                search: {
                  client_id: Number(clientId),
                  goal_id: goal.id,
                  modal: "proposal" as const,
                  state: "init",
                },
              });
            }}
          >
            Create Proposal
          </Button>
        </div>
      </DialogContent>
    </V2Dialog>
  );
}
